import type {
  CoachFrameworkReference,
  CoachIdentityIngestionIssue,
  CoachIdentityReference,
  CoachPhilosophyProfile,
  CoachVoiceProfile,
} from "@/lib/behavior/behaviorContracts";
import {
  COACH_IDENTITY_INPUT_RULES,
  COACH_IDENTITY_TEMPLATE_VERSION,
  normalizeCoachIdentityDocumentReference,
  normalizeCoachIdentityLinkReference,
  summarizeCoachIdentityIngestion,
} from "@/lib/behavior/coachIdentityIngestion";
import {
  bulletBlock,
  escapeTemplateValue,
  parseBulletListSection,
  parseMarkdownSections,
  trimTemplateText,
} from "@/lib/behavior/templateParser";

export interface CoachIdentityTemplateProfile {
  lensName: string;
  philosophy: string;
  principles: string[];
  frameworks: CoachFrameworkReference[];
  preferredLanguage: string[];
  avoidLanguage: string[];
  voiceSummary: string;
  questionStems: string[];
  phraseBank: string[];
  coachingMoves: string[];
  guardrails: string[];
  referenceLinks: Array<{
    title: string;
    url: string;
    notes: string[];
  }>;
  attachedDocuments: Array<{
    title: string;
    fileName: string;
    notes: string[];
  }>;
}

export interface CoachIdentityTemplatePreview {
  lensLabel: string;
  philosophySummary: string | null;
  voiceSummary: string | null;
  referenceCount: number;
  parsedReferenceCount: number;
}

export interface CoachIdentityTemplateParseResult {
  ok: boolean;
  rawSource: string;
  profile: CoachIdentityTemplateProfile;
  philosophyProfile: CoachPhilosophyProfile;
  voiceProfile: CoachVoiceProfile;
  references: CoachIdentityReference[];
  issues: CoachIdentityIngestionIssue[];
  preview: CoachIdentityTemplatePreview;
}

const EMPTY_PROFILE: CoachIdentityTemplateProfile = {
  lensName: "",
  philosophy: "",
  principles: [],
  frameworks: [],
  preferredLanguage: [],
  avoidLanguage: [],
  voiceSummary: "",
  questionStems: [],
  phraseBank: [],
  coachingMoves: [],
  guardrails: [],
  referenceLinks: [],
  attachedDocuments: [],
};

function frameworkBlock(frameworks: CoachFrameworkReference[]): string {
  return frameworks.length > 0
    ? frameworks.map((framework) => `- ${escapeTemplateValue(framework.name)}${framework.summary ? `: ${escapeTemplateValue(framework.summary)}` : ""}`).join("\n")
    : "- GROW: Goal, Reality, Options, Way forward.";
}

function parseFrameworks(value: string): CoachFrameworkReference[] {
  return parseBulletListSection(value)
    .map((line) => {
      const [name, ...summaryParts] = line.split(":");
      const normalizedName = trimTemplateText(name);
      if (!normalizedName) return null;
      const summary = trimTemplateText(summaryParts.join(":"));
      return { name: normalizedName, summary };
    })
    .filter((item): item is CoachFrameworkReference => Boolean(item));
}

function parseReferenceLine(line: string): { label: string | null; source: string | null; notes: string[] } {
  const normalized = line.replace(/^[-*]\s*/, "").trim();
  if (!normalized) return { label: null, source: null, notes: [] };

  const [primary, ...noteParts] = normalized.split("|");
  const notes = noteParts
    .join("|")
    .split(";")
    .map((item) => item.trim())
    .filter(Boolean);
  const primaryText = primary.trim();
  const markdownLinkMatch = primaryText.match(/^\[([^\]]+)\]\((https?:\/\/[^)]+)\)$/i);
  if (markdownLinkMatch) {
    return {
      label: trimTemplateText(markdownLinkMatch[1]),
      source: trimTemplateText(markdownLinkMatch[2]),
      notes,
    };
  }

  const rawUrlMatch = primaryText.match(/^(https?:\/\/\S+)$/i);
  if (rawUrlMatch) {
    return {
      label: null,
      source: trimTemplateText(rawUrlMatch[1]),
      notes,
    };
  }

  return {
    label: trimTemplateText(primaryText),
    source: trimTemplateText(primaryText),
    notes,
  };
}

function buildPhilosophyProfile(profile: CoachIdentityTemplateProfile): CoachPhilosophyProfile {
  const philosophy = trimTemplateText(profile.philosophy);
  const lensName = trimTemplateText(profile.lensName);
  const intendedStandards = [
    philosophy,
    ...profile.principles.map((item) => trimTemplateText(item)),
    ...profile.frameworks.map((item) => trimTemplateText(item.summary)),
  ].filter((item): item is string => Boolean(item));

  let knowledgeCoverage: CoachPhilosophyProfile["lens"]["knowledgeCoverage"] = "none";
  if (profile.frameworks.length > 0) {
    const withSummaryCount = profile.frameworks.filter((item) => item.summary).length;
    knowledgeCoverage = withSummaryCount === profile.frameworks.length ? "full" : "partial";
  }

  return {
    present: Boolean(
      lensName
      || philosophy
      || profile.principles.length
      || profile.frameworks.length
      || profile.preferredLanguage.length
      || profile.avoidLanguage.length
    ),
    lens: {
      source: "declared_coach_lens",
      lensName,
      philosophy,
      frameworks: profile.frameworks,
      preferredLanguage: profile.preferredLanguage,
      avoidLanguage: profile.avoidLanguage,
      knowledgeCoverage,
    },
    intendedStandards,
  };
}

function buildVoiceProfile(profile: CoachIdentityTemplateProfile): CoachVoiceProfile {
  const voiceSummary = trimTemplateText(profile.voiceSummary);

  return {
    present: Boolean(
      voiceSummary
      || profile.questionStems.length
      || profile.phraseBank.length
      || profile.guardrails.length
      || profile.coachingMoves.length
      || profile.avoidLanguage.length
    ),
    source: "coach_twin_profile",
    voiceSummary,
    questionStems: profile.questionStems,
    phraseBank: [...profile.phraseBank, ...profile.coachingMoves],
    avoidList: profile.avoidLanguage,
    guardrails: profile.guardrails,
  };
}

function buildDefaultInstructions(): string {
  return `# How Coach Veera Uses This Template

This template defines how Veera thinks, speaks, and works with your leaders on your behalf.

## What Veera does with each section

**Philosophy (Your theory of change)**
Veera reads this to understand *why* you coach the way you do. It uses this framing for wording, question structure, preparation, and reflection style. Leader conclusions must stay grounded in evidence, not inferred from your philosophy.

**Frameworks**
If you list GROW, Veera structures conversations in GROW language. If you list Immunity to Change, Veera can use competing-commitment language when the evidence supports it. Be specific — vague entries have no effect.

**Preferred language**
These exact phrases appear in Veera's responses to leaders and to you. List words and phrases you actually use in sessions, not aspirational language.

**Avoid language**
Veera filters these from everything it produces. Include terms that feel clinical, directive, or out of character — anything that would sound wrong coming from you.

**Guardrails**
Hard limits. Veera will not cross these regardless of what a leader asks. Write these as direct prohibitions: "Never suggest a leader change their behavior without first surfacing the pattern to the coach."

## Tips for a strong template

- Philosophy should be 3–6 sentences minimum. A fragment gives Veera a fragment to work with.
- Frameworks work best when you name the specific model and briefly describe how you use it.
- You can update this template at any time. Veera turns imports into reviewable changes that you approve and save before they shape conversations.
`;
}

export function buildCoachIdentityTemplate(
  profile?: Partial<CoachIdentityTemplateProfile>,
): string {
  const merged: CoachIdentityTemplateProfile = {
    ...EMPTY_PROFILE,
    ...profile,
    principles: profile?.principles || [],
    frameworks: profile?.frameworks || [],
    preferredLanguage: profile?.preferredLanguage || [],
    avoidLanguage: profile?.avoidLanguage || [],
    questionStems: profile?.questionStems || [],
    phraseBank: profile?.phraseBank || [],
    coachingMoves: profile?.coachingMoves || [],
    guardrails: profile?.guardrails || [],
    referenceLinks: profile?.referenceLinks || [],
    attachedDocuments: profile?.attachedDocuments || [],
  };

  const referencesBlock = merged.referenceLinks.length > 0
    ? merged.referenceLinks
      .map((reference) => `- [${escapeTemplateValue(reference.title)}](${escapeTemplateValue(reference.url)})${reference.notes.length > 0 ? ` | ${reference.notes.join("; ")}` : ""}`)
      .join("\n")
    : "- [Systems coaching article](https://example.com/article) | Prioritize relationship-system language.";

  const documentsBlock = merged.attachedDocuments.length > 0
    ? merged.attachedDocuments
      .map((reference) => `- ${escapeTemplateValue(reference.fileName)}${reference.notes.length > 0 ? ` | ${reference.notes.join("; ")}` : ""}`)
      .join("\n")
    : "- coaching-playbook.pdf | Reference only until parsed and confirmed.";

  return [
    "# Coach Identity Template",
    `Version: ${COACH_IDENTITY_TEMPLATE_VERSION}`,
    "",
    buildDefaultInstructions(),
    "",
    "## Lens Name",
    merged.lensName || "Warm Challenger",
    "",
    "## Philosophy",
    merged.philosophy || "Describe the developmental stance you want Veera to carry forward.",
    "",
    "## Principles And Commitments",
    bulletBlock(merged.principles, "Name the pattern before offering structure."),
    "",
    "## Frameworks",
    frameworkBlock(merged.frameworks),
    "",
    "## Preferred Language",
    bulletBlock(merged.preferredLanguage, "name the pattern"),
    "",
    "## Avoid Language",
    bulletBlock(merged.avoidLanguage, "fixing"),
    "",
    "## Voice Summary",
    merged.voiceSummary || "Describe how you want Veera to sound in 2-3 sentences.",
    "",
    "## Question Stems",
    bulletBlock(merged.questionStems, "What are you noticing here?"),
    "",
    "## Phrase Bank",
    bulletBlock(merged.phraseBank, "Let's slow that down."),
    "",
    "## Coaching Moves",
    bulletBlock(merged.coachingMoves, "Mirror the leader's language before reframing."),
    "",
    "## Guardrails",
    bulletBlock(merged.guardrails, "Do not over-prescribe."),
    "",
    "## Links",
    referencesBlock,
    "",
    "## Attached Documents",
    documentsBlock,
    "",
  ].join("\n");
}

export function parseCoachIdentityTemplate(rawSource: string): CoachIdentityTemplateParseResult {
  const normalized = rawSource.replace(/\r/g, "");
  const sections = parseMarkdownSections(normalized);
  const profile: CoachIdentityTemplateProfile = {
    lensName: sections.get("lens name") || "",
    philosophy: sections.get("philosophy") || "",
    principles: parseBulletListSection(sections.get("principles and commitments") || ""),
    frameworks: parseFrameworks(sections.get("frameworks") || ""),
    preferredLanguage: parseBulletListSection(sections.get("preferred language") || ""),
    avoidLanguage: parseBulletListSection(sections.get("avoid language") || ""),
    voiceSummary: sections.get("voice summary") || "",
    questionStems: parseBulletListSection(sections.get("question stems") || ""),
    phraseBank: parseBulletListSection(sections.get("phrase bank") || ""),
    coachingMoves: parseBulletListSection(sections.get("coaching moves") || ""),
    guardrails: parseBulletListSection(sections.get("guardrails") || ""),
    referenceLinks: parseBulletListSection(sections.get("links") || "")
      .map(parseReferenceLine)
      .filter((item) => item.source)
      .map((item) => ({
        title: item.label || item.source || "",
        url: item.source || "",
        notes: item.notes,
      })),
    attachedDocuments: parseBulletListSection(sections.get("attached documents") || "")
      .map(parseReferenceLine)
      .filter((item) => item.source)
      .map((item) => ({
        title: item.label || item.source || "",
        fileName: item.source || "",
        notes: item.notes,
      })),
  };

  const philosophyProfile = buildPhilosophyProfile(profile);
  const voiceProfile = buildVoiceProfile(profile);
  const references: CoachIdentityReference[] = [
    ...profile.referenceLinks.map((reference) => normalizeCoachIdentityLinkReference({
      title: reference.title,
      sourceUrl: reference.url,
      extractedNotes: reference.notes,
    })),
    ...profile.attachedDocuments.map((reference) => normalizeCoachIdentityDocumentReference({
      title: reference.title,
      fileName: reference.fileName,
      extractedNotes: reference.notes,
    })),
  ];
  const summary = summarizeCoachIdentityIngestion({
    philosophyProfile,
    voiceProfile,
    references,
  });
  const issues = [...summary.issues];

  if (!normalized.includes(COACH_IDENTITY_TEMPLATE_VERSION)) {
    issues.unshift({
      level: "error",
      field: "version",
      message: "Template version marker is missing.",
    });
  }

  if (!philosophyProfile.present && !voiceProfile.present && references.length === 0) {
    issues.push({
      level: "error",
      field: "template",
      message: "Template did not contain any usable identity content.",
    });
  }

  const ok = !issues.some((issue) => issue.level === "error");

  return {
    ok,
    rawSource,
    profile,
    philosophyProfile,
    voiceProfile,
    references,
    issues,
    preview: {
      lensLabel: philosophyProfile.lens.lensName || "Custom coach identity",
      philosophySummary: philosophyProfile.lens.philosophy,
      voiceSummary: voiceProfile.voiceSummary,
      referenceCount: references.length,
      parsedReferenceCount: references.filter((reference) => reference.status === "parsed").length,
    },
  };
}
